import {
  type AuthConfigResponse,
  authConfigSchema,
  DEV_PERSONAS,
  type DevLoginBody,
  demoEmail,
  type LoginError,
  meResponseSchema,
  type Role,
} from '@hrms/shared'
import { useQuery } from '@tanstack/react-query'
import { type FormEvent, useId, useState } from 'react'
import { FcGoogle } from 'react-icons/fc'
import { LuCircleAlert, LuWrench } from 'react-icons/lu'
import { Navigate, useNavigate, useSearchParams } from 'react-router'
import { Tooltip } from 'react-tooltip'
import { useAuth } from '../auth/AuthProvider.tsx'
import { Button, buttonClass } from '../components/ui/Button.tsx'
import { ApiError, apiGet, apiSend } from '../lib/api.ts'
import { cn } from '../lib/cn.ts'
import { HINT_ID, hint } from '../lib/hint.ts'
import { Logo } from '../shell/Brand.tsx'
import { ThemeToggle } from '../theme/ThemeToggle.tsx'

const ERROR_TEXT: Record<LoginError, (domain: string | null) => string> = {
  domain_not_allowed: (d) =>
    d
      ? `That Google account isn’t a @${d} account. Sign in with your work account.`
      : 'That Google account isn’t allowed to sign in.',
  archived: () =>
    'Your access to HR desk has been archived. Contact the HR team if you think this is a mistake.',
  oauth_failed: () => 'Google sign-in didn’t complete. Please try again.',
  google_disabled: () => 'Google sign-in isn’t set up on this server yet.',
}

/** Only same-app paths, never "//other.site". */
function safeNext(value: string | null): string {
  return value?.startsWith('/') && !value.startsWith('//') ? value : '/'
}

function useAuthConfig() {
  return useQuery<AuthConfigResponse>({
    queryKey: ['auth-config'],
    queryFn: () => apiGet('/auth/config', (d) => authConfigSchema.parse(d)),
    staleTime: Number.POSITIVE_INFINITY,
  })
}

type Access = '' | 'member' | 'admin' | 'lead' | 'viewer'
const ACCESS_OPTIONS: { value: Access; label: string }[] = [
  { value: '', label: 'Keep current access' },
  { value: 'member', label: 'Employee (member)' },
  { value: 'admin', label: 'HR team (admin)' },
  { value: 'lead', label: 'HR lead (admin)' },
  { value: 'viewer', label: 'Viewer' },
]

function accessToBody(access: Access): Pick<DevLoginBody, 'role' | 'isHrLead'> {
  if (!access) return {}
  if (access === 'lead') return { role: 'admin', isHrLead: true }
  return { role: access as Role, isHrLead: false }
}

const field =
  'h-8.5 w-full rounded-ctl border border-border-strong bg-surface px-3 text-sm text-text outline-none focus:border-accent focus:shadow-[0_0_0_3px_var(--accent-soft)]'

function DevLoginPanel({ domain, next }: { domain: string | null; next: string }) {
  const { setUser } = useAuth()
  const navigate = useNavigate()
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [access, setAccess] = useState<Access>('')
  const emailId = useId()
  const accessId = useId()

  const signIn = async (key: string, body: DevLoginBody) => {
    setBusy(key)
    setError(null)
    try {
      const { user } = await apiSend('POST', '/auth/dev-login', body, (d) =>
        meResponseSchema.parse(d),
      )
      setUser(user)
      navigate(next, { replace: true })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Sign-in failed')
      setBusy(null)
    }
  }

  const onCustom = (e: FormEvent) => {
    e.preventDefault()
    const value = email.trim()
    if (!value) return setError('Enter an email address')
    signIn('custom', {
      email: value.includes('@') ? value : demoEmail(value, domain),
      ...accessToBody(access),
    })
  }

  return (
    <section
      aria-labelledby="dev-login-title"
      data-color="amber"
      className="mt-6 rounded-card border border-dashed border-note-border bg-note-bg p-4 text-left"
    >
      <h2 id="dev-login-title" className="flex items-center gap-2 text-sm text-cf">
        <LuWrench aria-hidden /> Developer sign-in
      </h2>
      <p className="mt-1 text-[12.5px] text-muted">
        Development only: sign in as a demo person without Google. Turned off in production.
      </p>

      <div className="mt-3 grid grid-cols-2 gap-2 max-[420px]:grid-cols-1">
        {DEV_PERSONAS.map((p) => {
          const personaEmail = demoEmail(p.handle, domain)
          return (
            <Button
              key={p.handle}
              size="sm"
              disabled={busy !== null}
              onClick={() => signIn(p.handle, { email: personaEmail })}
              {...hint(personaEmail, 'top')}
            >
              {busy === p.handle ? 'Signing in…' : p.label}
            </Button>
          )
        })}
      </div>

      <form onSubmit={onCustom} className="mt-3 flex flex-col gap-2" noValidate>
        <label htmlFor={emailId} className="text-[12.5px] font-medium text-text">
          Or any email
        </label>
        <input
          id={emailId}
          type="email"
          autoComplete="off"
          placeholder={demoEmail('someone', domain)}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={field}
        />
        <label htmlFor={accessId} className="sr-only">
          Access
        </label>
        <div className="flex gap-2">
          <select
            id={accessId}
            value={access}
            onChange={(e) => setAccess(e.target.value as Access)}
            className={cn(field, 'min-w-0 flex-1')}
          >
            {ACCESS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <Button type="submit" variant="primary" disabled={busy !== null}>
            {busy === 'custom' ? 'Signing in…' : 'Sign in'}
          </Button>
        </div>
      </form>

      {error && (
        <p role="alert" className="mt-2 text-[12.5px] text-danger">
          {error}
        </p>
      )}
    </section>
  )
}

export function LoginPage() {
  const { user, status } = useAuth()
  const [params] = useSearchParams()
  const config = useAuthConfig()
  const next = safeNext(params.get('next'))
  const errorCode = params.get('error') as LoginError | null
  const domain = config.data?.allowedDomain ?? null

  if (status === 'ready' && user) return <Navigate to={next} replace />

  const errorText = errorCode && ERROR_TEXT[errorCode]?.(domain)
  const googleEnabled = config.data?.googleEnabled ?? false
  const googleHref = `/api/auth/google${next === '/' ? '' : `?next=${encodeURIComponent(next)}`}`

  return (
    <div className="flex min-h-dvh flex-col">
      <div className="flex justify-end p-3 pt-[max(12px,env(safe-area-inset-top))]">
        <ThemeToggle />
      </div>
      <main className="flex flex-1 items-start justify-center px-4 pt-[8vh] pb-16">
        <div className="w-full max-w-[400px] rounded-panel border border-border bg-surface p-7 text-center shadow-pop max-[420px]:p-5">
          <Logo className="mx-auto h-11" />
          <h1 className="mt-4 text-[22px] leading-tight">Sign in to HR desk</h1>
          <p className="mt-1 text-muted">HR desk: raise and track requests with the HR team.</p>

          {errorText && (
            <div
              role="alert"
              data-color="rose"
              className="mt-5 flex items-start gap-2 rounded-card bg-cb px-3 py-2.5 text-left text-[13px] text-cf"
            >
              <LuCircleAlert aria-hidden className="mt-0.5 shrink-0 text-base" />
              <span>{errorText}</span>
            </div>
          )}

          <div className="mt-6">
            {googleEnabled ? (
              <a
                href={googleHref}
                className={cn(buttonClass('default'), 'h-10 w-full text-[15px] no-underline')}
              >
                <FcGoogle aria-hidden className="text-xl" /> Sign in with Google
              </a>
            ) : (
              <span
                className="block"
                {...hint('Google sign-in isn’t configured on this server yet')}
              >
                <Button className="h-10 w-full text-[15px]" disabled>
                  <FcGoogle aria-hidden className="text-xl" /> Sign in with Google
                </Button>
              </span>
            )}
            <p className="mt-2.5 text-[12.5px] text-muted">
              {domain ? (
                <>
                  Use your <strong className="font-semibold text-text">@{domain}</strong> account
                </>
              ) : (
                'Use your work Google account'
              )}
            </p>
          </div>

          {config.data?.devLogin && <DevLoginPanel domain={domain} next={next} />}
        </div>
      </main>
      <Tooltip
        id={HINT_ID}
        className="hint-tip"
        opacity={1}
        delayShow={250}
        arrowColor="var(--text)"
      />
    </div>
  )
}
